import React from 'react';
import ReactDOM from 'react-dom/client';
import { ClerkProvider } from '@clerk/react';
import { dark } from '@clerk/ui/themes';
import { CLERK_SCRIPT_PINS } from './features/auth/clerkScripts';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { queryClient } from './queryClient';
import { idbPersister, shouldDehydrateQuery } from './queryPersister';
import { CacheOwnerGuard } from './features/auth/CacheOwnerGuard';
import { clerkAppearance } from './theme';
import './styles/theme.css';
import './styles/tailwind.css';
import './styles/globals.css';
import App from './App';

// Silence the benign "ResizeObserver loop completed with undelivered
// notifications" error. Browsers themselves ignore it; CRA's dev-error
// overlay escalates it to a full-screen modal. Fires when a layout
// change inside a ResizeObserver callback triggers another layout
// (common with recharts' ResponsiveContainer + any chart that reflows
// on data change). Harmless — production never sees it. See
// https://github.com/WICG/resize-observer/issues/38.
if (typeof window !== 'undefined') {
  // Suppress ResizeObserver error overlay in CRA 5 dev mode.
  // CRA's overlay uses window.onerror, not addEventListener.
  const origOnError = window.onerror;
  window.onerror = function (msg, ...args) {
    if (typeof msg === 'string' && msg.startsWith('ResizeObserver loop')) return true;
    return origOnError ? origOnError.call(this, msg, ...args) : false;
  };
  window.addEventListener('error', (e) => {
    if (e.message?.startsWith('ResizeObserver loop')) {
      e.stopImmediatePropagation();
      e.preventDefault();
    }
  }, true);
}

const CLERK_PUBLISHABLE_KEY = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;

if (!CLERK_PUBLISHABLE_KEY) {
  // Crash loudly in dev so the missing key is obvious immediately.
  // In production this would be a deploy misconfiguration — same treatment.
  throw new Error(
    '[Chess Deck] REACT_APP_CLERK_PUBLISHABLE_KEY is not set.\n' +
    'Add it to frontend/.env.local and restart the dev server.\n' +
    'See .env.local.example for the key format.'
  );
}

const root = ReactDOM.createRoot(document.getElementById('root')!);
root.render(
  <React.StrictMode>
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister: idbPersister,
        maxAge: 1000 * 60 * 60 * 24,   // drop cache older than 24h; matches gcTime
        // bump to invalidate all persisted caches on a shape change. v2 (2026-10-13): owner-tagged, home-only
        // (queryPersister.ts); this drops every v1 blob, which held whole histories (up to ~28 MB). v3 (2026-10-30, #362):
        // the home tile's Insights are the combined rows, under a new key and shape.
        buster: "v3",
        dehydrateOptions: { shouldDehydrateQuery },
      }}
    >
      <ClerkProvider
        {...CLERK_SCRIPT_PINS}
        publishableKey={CLERK_PUBLISHABLE_KEY}
        appearance={{ theme: dark, ...clerkAppearance }}
        afterSignOutUrl="/"
      >
        <CacheOwnerGuard />
        <App />
      </ClerkProvider>
    </PersistQueryClientProvider>
  </React.StrictMode>
);
